import { useEffect, useState } from "react";
import { Loader2, Save, MoonStar } from "lucide-react";
import { toast } from "sonner";

import { notificationService } from "@/services/notificationService";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const GROUPS = [
  ["leads_followups", "Leads & Follow-Ups"],
  ["tasks_calendar", "Tasks & Calendar"],
  ["transactions", "Transactions"],
  ["compliance", "License & Compliance"],
  ["marketing", "Marketing"],
  ["goals_reviews", "Goals & Business Reviews"],
  ["product_system", "Product & System"],
];

const Toggle = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    <span className="text-xs text-muted-foreground">{label}</span>
  </div>
);

export const NotificationPreferences = () => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { notificationService.getPreferences().then(setPrefs).catch(() => toast.error("Could not load preferences")); }, []);

  if (!prefs) return <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>;

  const setCat = (grp, ch, val) => setPrefs((p) => ({ ...p, categories: { ...p.categories, [grp]: { ...p.categories[grp], [ch]: val } } }));
  const setDigest = (key, ch, val) => setPrefs((p) => ({ ...p, [key]: { ...p[key], [ch]: val } }));
  const setQuiet = (k, val) => setPrefs((p) => ({ ...p, quiet_hours: { ...p.quiet_hours, [k]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      const saved = await notificationService.updatePreferences({
        categories: prefs.categories, daily_brief: prefs.daily_brief,
        weekly_review: prefs.weekly_review, quiet_hours: prefs.quiet_hours,
      });
      setPrefs(saved);
      toast.success("Notification preferences saved");
    } catch (err) { toast.error(err?.message || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="notification-preferences">
      <Card>
        <CardHeader><CardTitle className="text-base">By category</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <div className="hidden grid-cols-[1fr_auto_auto] gap-x-8 pb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
            <span /><span className="text-center">In-app</span><span className="text-center">Email</span>
          </div>
          {GROUPS.map(([grp, label]) => (
            <div key={grp} className="flex flex-col gap-2 border-b border-border py-3 last:border-0 sm:grid sm:grid-cols-[1fr_auto_auto] sm:items-center sm:gap-x-8">
              <span className="text-sm font-medium text-foreground">{label}</span>
              <Toggle label="In-app" checked={prefs.categories[grp]?.in_app} onChange={(v) => setCat(grp, "in_app", v)} testid={`pref-${grp}-in_app`} />
              <Toggle label="Email" checked={prefs.categories[grp]?.email} onChange={(v) => setCat(grp, "email", v)} testid={`pref-${grp}-email`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Business communications</CardTitle></CardHeader>
        <CardContent className="space-y-3 pt-0">
          <div className="flex flex-col gap-2 border-b border-border py-2 sm:flex-row sm:items-center sm:justify-between">
            <div><p className="text-sm font-medium text-foreground">Daily Business Brief</p><p className="text-xs text-muted-foreground">A short deterministic summary each morning.</p></div>
            <div className="flex gap-6">
              <Toggle label="In-app" checked={prefs.daily_brief?.in_app} onChange={(v) => setDigest("daily_brief", "in_app", v)} testid="pref-daily-in_app" />
              <Toggle label="Email" checked={prefs.daily_brief?.email} onChange={(v) => setDigest("daily_brief", "email", v)} testid="pref-daily-email" />
            </div>
          </div>
          <div className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div><p className="text-sm font-medium text-foreground">Weekly Business Review</p><p className="text-xs text-muted-foreground">Your week in review and what's ahead.</p></div>
            <div className="flex gap-6">
              <Toggle label="In-app" checked={prefs.weekly_review?.in_app} onChange={(v) => setDigest("weekly_review", "in_app", v)} testid="pref-weekly-in_app" />
              <Toggle label="Email" checked={prefs.weekly_review?.email} onChange={(v) => setDigest("weekly_review", "email", v)} testid="pref-weekly-email" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><MoonStar className="h-4 w-4 text-accent" /> Quiet hours</CardTitle></CardHeader>
        <CardContent className="space-y-3 pt-0">
          <Toggle label="Pause non-critical emails during quiet hours" checked={prefs.quiet_hours?.enabled} onChange={(v) => setQuiet("enabled", v)} testid="pref-quiet-enabled" />
          <div className="grid grid-cols-2 gap-3 sm:max-w-xs">
            <div><label className="text-xs text-muted-foreground">From</label><Input type="time" value={prefs.quiet_hours?.start || "21:00"} onChange={(e) => setQuiet("start", e.target.value)} data-testid="pref-quiet-start" /></div>
            <div><label className="text-xs text-muted-foreground">To</label><Input type="time" value={prefs.quiet_hours?.end || "07:00"} onChange={(e) => setQuiet("end", e.target.value)} data-testid="pref-quiet-end" /></div>
          </div>
          <p className="text-[11px] text-muted-foreground">Interpreted in your local time. Critical account and security messages are never suppressed. (An explicit workspace timezone setting arrives in a later phase.)</p>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="pref-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save preferences</Button>
      </div>
    </div>
  );
};
